import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Layers, Sparkles, GitBranch, ShieldCheck, Boxes, Gauge } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { VideoEmbed } from "../components/VideoEmbed";
import { ASSETS, PROBLEMS, FORCES, LAYERS, DIFFERENTIATORS, STATS } from "../data/content";

const Overline = ({ children }) => (
  <span className="font-mono-el text-xs tracking-[0.25em] uppercase text-[#4FC3F7]">{children}</span>
);

const diffIcons = [Sparkles, Layers, Boxes, ShieldCheck, Gauge, GitBranch];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* ---------- HERO ---------- */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={ASSETS.heroBg}
            alt=""
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#020817] via-[#020817]/85 to-[#020817]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#020817] via-transparent to-[#020817]/60" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-28 pb-20">
          <div className="max-w-3xl">
            <div className="animate-fade-up" style={{ animationDelay: "100ms" }}>
              <Overline>EtherLake · The Apeiron Lakehouse</Overline>
            </div>
            <h1
              className="mt-6 text-5xl md:text-6xl lg:text-7xl font-light leading-[1.02] tracking-tight text-white animate-fade-up text-glow"
              style={{ animationDelay: "200ms" }}
            >
              Unify data.
              <br />
              <span className="gradient-text font-medium">Unlock possibilities.</span>
            </h1>
            <p
              className="mt-8 text-lg md:text-xl text-[#94A3B8] leading-relaxed max-w-2xl animate-fade-up"
              style={{ animationDelay: "320ms" }}
            >
              Apeiron is the agent-first lakehouse — built entirely on open standards, running in
              your own cloud. You describe what you want; AI agents handle ingestion, transformation,
              analytics, and activation end to end.
            </p>
            <div
              className="mt-10 flex flex-col sm:flex-row gap-4 animate-fade-up"
              style={{ animationDelay: "440ms" }}
            >
              <Link
                to="/contact"
                data-testid="hero-cta-button"
                className="group inline-flex items-center justify-center gap-2 bg-[#4FC3F7] text-[#020817] hover:bg-[#00E5FF] transition-colors duration-300 px-8 py-4 font-semibold"
              >
                Request a Demo
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/product"
                data-testid="hero-secondary-button"
                className="inline-flex items-center justify-center gap-2 border border-white/20 bg-white/5 backdrop-blur-sm text-white hover:bg-white/10 transition-colors duration-300 px-8 py-4 font-medium"
              >
                Explore Apeiron
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#4FC3F7]/40 to-transparent z-10" />
      </section>

      {/* ---------- STATS BAND ---------- */}
      <section className="relative z-10 border-b border-white/10 bg-[#0A1A2F]/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 80} className="py-10 px-6 first:pl-0">
              <div className="text-4xl md:text-5xl font-light gradient-text">{s.value}</div>
              <div className="mt-3 text-sm text-[#94A3B8] leading-snug">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------- VIDEO ---------- */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <Reveal className="text-center">
            <Overline>See It In Action</Overline>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white">
              One intent. <span className="gradient-text">Agents do the rest.</span>
            </h2>
            <p className="mt-6 text-[#94A3B8] text-lg max-w-2xl mx-auto leading-relaxed">
              Watch how Apeiron turns a plain-language request into a fully orchestrated data
              pipeline — ingestion to activation, running in your own cloud.
            </p>
          </Reveal>
          <Reveal delay={120} className="mt-14">
            <VideoEmbed videoId="dXYJHWsKdDQ" title="Apeiron by EtherLake" />
          </Reveal>
        </div>
      </section>

      {/* ---------- PROBLEM ---------- */}
      <section className="relative py-24 md:py-32 grid-bg">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>The Problem</Overline>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white max-w-3xl">
              Three problems. <span className="text-[#94A3B8]">One broken foundation.</span>
            </h2>
            <p className="mt-6 text-[#94A3B8] text-lg max-w-2xl leading-relaxed">
              The modern data stack was designed for a world of batch analytics and human-operated
              pipelines. That world is over — and you can't patch your way out of the wrong foundation.
            </p>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PROBLEMS.map((p, i) => (
              <Reveal
                key={p.no}
                delay={i * 120}
                className="group bg-white/5 backdrop-blur-md border border-white/10 p-8 md:p-10 hover:-translate-y-1 hover:border-[#4FC3F7]/30 transition-all duration-300"
              >
                <div className="font-mono-el text-sm text-[#4FC3F7]">{p.no}</div>
                <h3 className="mt-4 text-xl md:text-2xl font-medium text-white">{p.title}</h3>
                <p className="mt-4 text-[#94A3B8] leading-relaxed text-base">{p.body}</p>
                <p className="mt-6 pt-6 border-t border-white/10 text-sm text-[#64748B] italic">
                  “{p.quote}”
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- WHY NOW ---------- */}
      <section className="relative py-24 md:py-32 bg-[#0A1A2F]/30 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>Why Now</Overline>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white max-w-3xl">
              Three forces are converging — defining a window incumbents cannot close.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
            {FORCES.map((f, i) => (
              <Reveal
                key={f.no}
                delay={i * 120}
                className="relative bg-gradient-to-b from-white/5 to-transparent border border-white/10 p-8 md:p-10 overflow-hidden group hover:border-[#4FC3F7]/30 transition-colors duration-300"
              >
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-[#4FC3F7]/10 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <div className="font-mono-el text-xs tracking-[0.2em] uppercase text-[#475569]">
                  {f.no}
                </div>
                <h3 className="mt-4 text-2xl font-medium text-white">{f.title}</h3>
                <p className="mt-4 text-[#94A3B8] leading-relaxed">{f.body}</p>
                <div className="mt-8 inline-flex items-center gap-2 text-sm text-[#4FC3F7] font-mono-el">
                  <span className="w-6 h-px bg-[#4FC3F7]" />
                  {f.solves}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- ARCHITECTURE ---------- */}
      <section className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>The Architecture</Overline>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white max-w-3xl">
              You express intent. <span className="gradient-text">Agents orchestrate everything below.</span>
            </h2>
            <p className="mt-6 text-[#94A3B8] text-lg max-w-2xl">
              The only proprietary layer is the agent. Everything beneath it is open source or
              bring-your-own — so your data stays readable, portable, and yours.
            </p>
          </Reveal>

          <div className="mt-16 space-y-4">
            {LAYERS.map((layer, i) => (
              <Reveal
                key={layer.name}
                delay={i * 100}
                className={`relative grid grid-cols-1 lg:grid-cols-12 gap-6 items-center p-8 border transition-colors duration-300 ${
                  layer.proprietary
                    ? "bg-gradient-to-r from-[#1565C0]/20 to-transparent border-[#4FC3F7]/30"
                    : "bg-white/5 border-white/10 hover:border-white/20"
                }`}
              >
                <div className="lg:col-span-3">
                  <div className="flex items-center gap-3">
                    <span className="font-mono-el text-xs text-[#4FC3F7]">0{i + 1}</span>
                    <h3 className="text-xl md:text-2xl font-medium text-white">{layer.name}</h3>
                  </div>
                  <p className="mt-2 text-sm text-[#94A3B8]">{layer.tag}</p>
                  {layer.proprietary && (
                    <span className="mt-3 inline-block font-mono-el text-[10px] tracking-[0.2em] uppercase text-[#020817] bg-[#4FC3F7] px-2 py-1">
                      Apeiron IP
                    </span>
                  )}
                </div>
                <div className="lg:col-span-9 grid grid-cols-2 md:grid-cols-4 gap-3">
                  {layer.items.map((item) => (
                    <div
                      key={item}
                      className="text-sm text-[#CBD5E1] bg-[#020817]/50 border border-white/5 px-4 py-3"
                    >
                      {item}
                    </div>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- DIFFERENTIATORS ---------- */}
      <section className="relative py-24 md:py-32 bg-[#0A1A2F]/30 border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <Overline>What Makes Apeiron Different</Overline>
            <h2 className="mt-5 text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white max-w-3xl">
              Open by design. Boundless by architecture.
            </h2>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {DIFFERENTIATORS.map((d, i) => {
              const Icon = diffIcons[i % diffIcons.length];
              return (
                <Reveal
                  key={d.title}
                  delay={(i % 3) * 100}
                  className="group bg-white/5 border border-white/10 p-8 hover:-translate-y-1 hover:border-[#4FC3F7]/30 transition-all duration-300"
                >
                  <div className="w-11 h-11 flex items-center justify-center bg-[#4FC3F7]/10 border border-[#4FC3F7]/20 text-[#4FC3F7] mb-6 group-hover:bg-[#4FC3F7] group-hover:text-[#020817] transition-colors duration-300">
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-lg font-medium text-white">{d.title}</h3>
                  <p className="mt-3 text-[#94A3B8] text-sm leading-relaxed">{d.body}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <CtaBand />
    </div>
  );
}

export const CtaBand = () => (
  <section className="relative py-28 md:py-36 overflow-hidden">
    <div className="absolute inset-0 grid-bg opacity-60" />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-[#1565C0]/20 blur-[120px]" />
    <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
      <Reveal>
        <span className="font-mono-el text-xs tracking-[0.25em] uppercase text-[#4FC3F7]">
          Early Access · Design Partners
        </span>
        <h2 className="mt-6 text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-white leading-[1.05]">
          Help shape a <span className="gradient-text font-medium">boundless</span> lakehouse.
        </h2>
        <p className="mt-6 text-lg text-[#94A3B8] max-w-2xl mx-auto leading-relaxed">
          Apeiron is in early development as a working prototype. We're partnering with a small
          group of teams to shape the product ahead of broader availability.
        </p>
        <Link
          to="/contact"
          data-testid="cta-band-button"
          className="mt-10 group inline-flex items-center gap-2 bg-[#4FC3F7] text-[#020817] hover:bg-[#00E5FF] transition-colors duration-300 px-10 py-4 font-semibold"
        >
          Request a Demo
          <ArrowUpRight className="w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
      </Reveal>
    </div>
  </section>
);
